import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { useReviewDetailQuery } from '@/entities/review';
import { HideReviewDialog, UnhideReviewButton } from '@/features/review/moderation';
import { formatDateTime } from '@/shared/lib';
import { EmptyState, StatusBadge } from '@/shared/ui';
import { ReviewHiddenHistory } from './review-hidden-history';

interface ReviewDetailProps {
  readonly reviewId: string;
}

/**
 * 어드민 후기 상세 — 내용·사진·작성자 실명/이메일·주문 번호·숨김 이력, 상태에 따라 숨기기/다시 공개.
 * 열 때마다 서버가 개인정보 조회 기록을 남기므로 훅이 다시 부르지 않게 되어 있다.
 */
export const ReviewDetail = ({ reviewId }: ReviewDetailProps): ReactElement => {
  const query = useReviewDetailQuery(reviewId);

  if (query.isPending) return <EmptyState>불러오는 중…</EmptyState>;
  if (query.isError) {
    return <EmptyState isError>후기를 불러오지 못했습니다: {query.error.message}</EmptyState>;
  }
  const review = query.data;
  const hidden = review.status === 'HIDDEN';

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <StatusBadge tone={hidden ? 'danger' : 'positive'}>
            {hidden ? '숨김' : '공개'}
          </StatusBadge>
          <span className="text-sm text-muted-foreground">
            {review.productName} · {review.rating} / 5점
          </span>
        </div>
        {hidden ? <UnhideReviewButton review={review} /> : <HideReviewDialog review={review} />}
      </div>

      {review.hidden ? <ReviewHiddenHistory hidden={review.hidden} /> : null}

      <section className="rounded-2xl border border-foreground/[0.06] bg-card px-5 py-4">
        <p className="whitespace-pre-wrap text-sm">{review.content}</p>
        {review.images.length > 0 ? (
          <ul className="mt-4 flex flex-wrap gap-2">
            {review.images.map((image) => (
              <li key={image.id}>
                <img
                  src={image.url}
                  alt="후기 사진"
                  width={image.widthPx}
                  height={image.heightPx}
                  className="size-28 rounded-lg object-cover"
                />
              </li>
            ))}
          </ul>
        ) : null}
        <p className="mt-4 text-xs text-muted-foreground">
          {review.colorName}
          {review.printMethodName ? ` · ${review.printMethodName}` : ''} · {review.quantity}개 ·
          작성 {formatDateTime(review.createdAt)}
          {review.editedAt ? ` · 수정 ${formatDateTime(review.editedAt)}` : ''}
        </p>
      </section>

      <dl className="grid grid-cols-[6rem_1fr] gap-y-2 rounded-2xl border border-foreground/[0.06] bg-card px-5 py-4 text-sm">
        <dt className="text-muted-foreground">작성자</dt>
        <dd className="flex items-center gap-2">
          {review.author.name}
          {review.author.withdrawn ? <StatusBadge tone="neutral">탈퇴한 회원</StatusBadge> : null}
        </dd>
        <dt className="text-muted-foreground">이메일</dt>
        <dd>{review.author.email}</dd>
        <dt className="text-muted-foreground">주문 번호</dt>
        <dd>
          <Link
            to={`/orders/${review.orderId}`}
            className="underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground"
          >
            {review.orderNumber}
          </Link>
        </dd>
      </dl>
    </div>
  );
};
